import { memo } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  FieldValueInput,
  ProfileFieldOption,
  resolveFieldType,
} from '../../../shared/shared-profile-field-filter';
import type { RulesEngineFormValues } from './rules-engine-form-page';
import { OPERATORS } from './condition-block-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

interface ValueInputRendererProps {
  blockIndex: number;
  conditionIndex: number;
  control: Control<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  customOptions: ProfileFieldOption[];
  domains?: string[];
  gPanelRoles: string[];
}

export const ValueInputRenderer = memo(function ValueInputRenderer({
  blockIndex,
  conditionIndex,
  control,
  customOptions,
  gPanelRoles,
}: ValueInputRendererProps) {
  const fieldName = useWatch({
    control,
    name: `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName` as any,
  });

  const fieldInfoType = useWatch({
    control,
    name: `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldInfoType` as any,
  });

  if (fieldInfoType === 'employeeInformation' && fieldName === 'gPanelRole') {
    return (
      <Controller
        name={
          `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value` as any
        }
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            select
            fullWidth
            size="small"
            label="gPanel Role"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            value={field.value || ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {(gPanelRoles || []).map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    );
  }

  return (
    <FieldValueInput
      control={control as any}
      name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
      fieldType={resolveFieldType(fieldName, customOptions)}
      sx={{ minWidth: 'unset', width: '100%' }}
      fullWidth
      size="small"
    />
  );
});

interface ConditionBlockRowProps {
  blockIndex: number;
  blockConditions: any[];
  conditionBlocks: any[];
  control: Control<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  getFieldOptions: (
    infoType: string
  ) => Array<{ label: string; value: string }>;
  customOptions: ProfileFieldOption[];
  fieldInfoTypes: { label: string; value: string }[];
  domains?: string[];
  triggerGroup?: string;
  isGroupAdminEvent?: boolean;
  gPanelRoles: string[];
}

const ConditionBlockRow = memo(function ConditionBlockRow({
  blockIndex,
  blockConditions,
  conditionBlocks,
  control,
  setValue,
  getFieldOptions,
  customOptions,
  fieldInfoTypes,
  domains = [],
  triggerGroup = '',
  isGroupAdminEvent = false,
  gPanelRoles,
}: ConditionBlockRowProps) {
  // Helper function to ensure a condition has a valid operator
  const ensureValidOperator = (condition: any): any => {
    if (!condition.operator) {
      const defaultOperator = 'contains';
      return { ...condition, operator: defaultOperator };
    }
    return condition;
  };

  const handleLogicalOperatorChange = (
    conditionIndex: number,
    operator: 'AND' | 'OR'
  ) => {
    const updatedConditions = [...blockConditions];
    updatedConditions[conditionIndex - 1] = {
      ...updatedConditions[conditionIndex - 1],
      logicalOperator: operator,
    };
    const updatedBlock = {
      ...conditionBlocks[blockIndex],
      conditions: updatedConditions,
    };
    const updatedBlocks = [...conditionBlocks];
    updatedBlocks[blockIndex] = updatedBlock;
    setValue('conditionBlocks', updatedBlocks);
  };

  const renderValueInput = (conditionIndex: number) => {
    return (
      <ValueInputRenderer
        blockIndex={blockIndex}
        conditionIndex={conditionIndex}
        control={control}
        setValue={setValue}
        customOptions={customOptions}
        domains={domains}
        gPanelRoles={gPanelRoles}
      />
    );
  };

  return (
    <Stack gap={2}>
      {blockConditions.map((condition: any, conditionIndex: number) => (
        <Box key={condition.id}>
          {/* Logical Operator before this condition (except for first) */}
          {conditionIndex > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mb: 3,
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  variant={
                    blockConditions[conditionIndex - 1]?.logicalOperator !==
                    'OR'
                      ? 'contained'
                      : 'outlined'
                  }
                  size="small"
                  sx={{ minWidth: '50px', textTransform: 'none' }}
                  onClick={() =>
                    handleLogicalOperatorChange(conditionIndex, 'AND')
                  }
                >
                  And
                </Button>
                <Button
                  variant={
                    blockConditions[conditionIndex - 1]?.logicalOperator ===
                    'OR'
                      ? 'contained'
                      : 'outlined'
                  }
                  size="small"
                  sx={{ minWidth: '50px', textTransform: 'none' }}
                  onClick={() =>
                    handleLogicalOperatorChange(conditionIndex, 'OR')
                  }
                >
                  Or
                </Button>
              </Box>
            </Box>
          )}

          {/* Condition Row */}
          <Grid
            container
            spacing={1}
            alignItems="center"
            sx={{
              pl: 2,
              borderLeft: '2px solid',
              borderLeftColor: 'primary.main',
            }}
          >
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Controller
                name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldInfoType`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="What kind of information?"
                    value={field.value || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val);

                      setValue(
                        `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`,
                        ''
                      );
                      // Reset value and operator when type changes
                      setValue(
                        `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                        ''
                      );
                      // Set default operator based on the new field type
                      const defaultOperator = 'contains';
                      setValue(
                        `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
                        defaultOperator
                      );
                    }}
                  >
                    {fieldInfoTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {triggerGroup === 'admin' && !isGroupAdminEvent && (
              <Grid
                size={{ xs: 12, sm: 6, md: 2 }}
                sx={{
                  display: 'block',
                }}
              >
                <Controller
                  name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`}
                  control={control}
                  render={({ field, fieldState }) => {
                    const infoType =
                      blockConditions[conditionIndex]?.fieldInfoType;
                    const fieldOptions = getFieldOptions(infoType);
                    return (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Which field?"
                        value={field.value || ''}
                        disabled={!infoType}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val);
                          setValue(
                            `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                            ''
                          );
                          const newOperator =
                            val === 'gPanelRole' || val === 'cpanelRole'
                              ? 'equals'
                              : 'contains';
                          setValue(
                            `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
                            newOperator
                          );
                        }}
                      >
                        {fieldOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Controller
                name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`}
                control={control}
                render={({ field }) => {
                  const fieldName =
                    blockConditions[conditionIndex]?.fieldName || '';

                  const isRoleField =
                    fieldName === 'gPanelRole' || fieldName === 'cpanelRole';

                  const validOperators = isRoleField
                    ? OPERATORS.filter(
                        (op) =>
                          op.value === 'equals' || op.value === 'notEquals'
                      )
                    : OPERATORS;

                  const defaultOperator = 'contains';
                  const operatorValue = field.value || defaultOperator;

                  return (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      label="Operator"
                      value={operatorValue}
                      onChange={(e) => {
                        const newValue = e.target.value || defaultOperator;
                        field.onChange(newValue);
                      }}
                    >
                      {validOperators.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 5,
              }}
            >
              {renderValueInput(conditionIndex)}
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 1 }}
              sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}
            >
              <Tooltip title="Delete condition">
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedConditions = blockConditions
                      .filter((_: any, i: number) => i !== conditionIndex)
                      .map((cond: any) => ensureValidOperator(cond));
                    if (updatedConditions.length === 0) {
                      setValue(
                        'conditionBlocks',
                        conditionBlocks.filter(
                          (_: any, index: number) => index !== blockIndex
                        )
                      );
                      return;
                    }
                    const updatedBlock = {
                      ...conditionBlocks[blockIndex],
                      conditions: updatedConditions,
                    };
                    const updatedBlocks = [...conditionBlocks];
                    updatedBlocks[blockIndex] = updatedBlock;
                    setValue('conditionBlocks', updatedBlocks);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Stack>
  );
});

export default ConditionBlockRow;
